

import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/ui/card";
import { TimeRangeDropdown } from "./time-range-dropdown";
import type { TimeRange, DateRange } from "../types";
import type { LucideIcon } from "lucide-react";

interface MetricCardProps {
  title: string;
  value: string | number;
  className?: string;
  timeRange?: TimeRange;
  customDateRange?: DateRange;
  onTimeRangeChange?: (range: TimeRange, dateRange?: DateRange) => void;
  icon?: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  description?: string;
  exportMode?: boolean;
  onClick?: () => void;
}

export function MetricCard({
  title,
  value,
  className = "",
  timeRange = 'current',
  customDateRange,
  onTimeRangeChange,
  icon: Icon,
  iconColor = "text-primary",
  iconBgColor = "bg-primary/10",
  description,
  exportMode = false,
  onClick,
}: MetricCardProps) {
  if (!exportMode) {
    return (
      <Card
        className={`metric-card-hover shadow-forest border-primary/10 hover:shadow-forest-lg ${onClick ? "cursor-pointer" : ""} ${className}`}
        onClick={onClick}
      >
        <CardContent className="flex items-center gap-3 p-3">
          {Icon && (
            <div className={`shrink-0 rounded-lg p-2 shadow-sm ${iconBgColor}`}>
              <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex h-6 items-center justify-between gap-2">
              <CardTitle className="truncate text-xs font-medium text-muted-foreground">{title}</CardTitle>
              {onTimeRangeChange && (
                <TimeRangeDropdown
                  value={timeRange}
                  onChange={onTimeRangeChange}
                  customDateRange={customDateRange}
                />
              )}
            </div>
            <div className="truncate text-2xl font-bold leading-tight text-gradient-primary">{value}</div>
            {description && (
              <p className="truncate text-[11px] text-muted-foreground" title={description}>
                {description}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`metric-card-hover shadow-forest border-primary/10 hover:shadow-forest-lg ${exportMode ? "dashboard-stat-card" : ""} ${onClick ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className={`p-2 rounded-lg shadow-sm ${iconBgColor}`}>
                <Icon className={`h-5 w-5 ${iconColor}`} />
              </div>
            )}
            <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          </div>
          {!exportMode && onTimeRangeChange && (
            <TimeRangeDropdown
              value={timeRange}
              onChange={onTimeRangeChange}
              customDateRange={customDateRange}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className={exportMode ? "text-2xl font-bold text-emerald-700" : "text-3xl font-bold text-gradient-primary"}>
          {value}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}
